import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getTextArticle } from "../api/articles";
import "./TextArticleView.css";

function formatContent(content) {
  if (!content) return "";
  
  // If content already contains HTML tags, return as is
  if (/<[a-z][\s\S]*>/i.test(content)) {
    return content;
  }
  
  // For plain text, convert line breaks to <br> tags and wrap paragraphs
  return content
    .split('\n\n')
    .map(paragraph => {
      const formatted = paragraph
        .split('\n')
        .filter(line => line.trim())
        .join('<br>');
      return formatted ? `<p>${formatted}</p>` : '';
    })
    .filter(p => p)
    .join('');
}

function TextArticleView() {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchArticle() {
      try {
        const articleData = await getTextArticle(articleId);
        setArticle(articleData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchArticle();
  }, [articleId]);

  if (loading) {
    return (
      <div className="text-article-view-container">
        <p className="loading-text">Loading article...</p>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="text-article-view-container">
        <p className="error-text">Error loading article: {error || "Article not found"}</p>
        <Link to="/articles" className="back-link">← Back to Articles</Link>
      </div>
    );
  }

  const formattedDate = article.article_date 
    ? new Date(article.article_date).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    : "";

  // Combine blocks and images, sort by ordinal
  const textBlocks = (article.text_article_block || []).map(block => ({
    type: 'text',
    ordinal: block.ordinal,
    content: block.content
  }));

  const imageBlocks = (article.text_article_image || []).map(img => ({
    type: 'image',
    ordinal: img.ordinal,
    url: img.url,
    imageId: img.image_id
  }));

  const allBlocks = [...textBlocks, ...imageBlocks].sort((a, b) => a.ordinal - b.ordinal);

  return (
    <div className="text-article-view-container">
      <Link to="/articles" className="back-link">← Back to Articles</Link>
      
      <article className="text-article-content">
        <header className="article-header">
          <h1 className="article-view-title">{article.article_title}</h1>
          {formattedDate && <p className="article-date">{formattedDate}</p>}
          <p className="article-view-description">{article.article_description}</p>
        </header>
        
        <div className="article-body">
          {allBlocks.map((block, index) => (
            block.type === 'text' ? (
              <div 
                key={`text-${index}`}
                dangerouslySetInnerHTML={{ __html: formatContent(block.content) }}
              />
            ) : (
              <div key={`image-${index}`} style={{ margin: "32px 0", textAlign: "center" }}>
                <img
                  src={block.url}
                  alt="Article content"
                  style={{ maxWidth: "100%", height: "auto", borderRadius: "8px" }}
                />
              </div>
            )
          ))}
        </div>
      </article>
    </div>
  );
}

export default TextArticleView;
